import type {
  ChatGeneratedPageItem,
  ChatMessageContent,
} from "schemas/generated/chatMessage";
import type { FileWithLatestFileVersion } from "schemas/generated/file";

import React, { useEffect, useState } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { useFetchPageFilesForWebContainer } from "@/features/web-container/useFetchPageFilesForWebContainer";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import {
  Card,
  CardContent,
} from "@replo/design-system/components/shadcn/core/card";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { useRealtimeRun } from "@trigger.dev/react-hooks";
import { CircleX } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { slugify } from "replo-utils/lib/string";

import { useIsChatLoading } from "../store/ChatStoreContext";
import { formatElapsedTime, useElapsedTime } from "../utils/elapsedTime";

function PageGenerationCard({
  status,
  startedAt,
  onViewPage,
  isChatLoading,
  pageName,
}: {
  status:
    | "COMPLETED"
    | "WAITING_FOR_DEPLOY"
    | "QUEUED"
    | "EXECUTING"
    | "REATTEMPTING"
    | "FROZEN"
    | "CANCELED"
    | "CRASHED"
    | "INTERRUPTED"
    | "SYSTEM_FAILURE"
    | "DELAYED"
    | "EXPIRED"
    | "TIMED_OUT"
    | undefined;
  startedAt?: Date;
  onViewPage?: () => void;
  isChatLoading?: boolean;
  pageName?: string;
}) {
  const elapsedTime = useElapsedTime(startedAt ?? null);
  const isCompleted = status === "COMPLETED";

  return (
    <div className="border-border border-2 rounded-lg max-w-[350px] flex flex-row gap-3 p-3 px-3 justify-between">
      <div className="flex flex-row gap-3">
        <img
          src="/images/page-generation.svg"
          alt="Page Generation Loading"
          className="w-10 h-10"
        />

        <div className="flex flex-col gap-1">
          <div className="header-h4">
            {isCompleted ? pageName : "Generating Page"}
          </div>
          <div className="text-muted text-sm">
            {isCompleted
              ? "Generation Completed"
              : "Building should take 2-3 min"}
          </div>
        </div>
      </div>

      {isCompleted ? (
        <Button
          variant="primary"
          size="sm"
          onClick={onViewPage}
          disabled={isChatLoading}
        >
          View Page
        </Button>
      ) : (
        <div>{formatElapsedTime(elapsedTime)}</div>
      )}
    </div>
  );
}

function GeneratingState({
  toolResult,
  toolMessageId,
}: {
  toolResult: Extract<ChatMessageContent[number], { type: "tool-result" }>;
  toolMessageId?: string;
}) {
  const projectId = useCurrentProjectId();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  const [isRunCompleted, setIsRunCompleted] = useState(false);
  const [completedRunData, setCompletedRunData] = useState<any>(null);

  const jobId = toolResult.result.jobId;
  const publicAccessToken = toolResult.result.publicAccessToken;

  const { run, error } = useRealtimeRun(jobId, {
    accessToken: publicAccessToken,
    enabled: Boolean(jobId && publicAccessToken && !isRunCompleted),
  });

  const fetchPageFiles = useFetchPageFilesForWebContainer();

  const { mutate: updateMessage } = trpc.chat.updateMessage.useMutation({
    onSuccess: () => {
      void trpcUtils.chat.get.invalidate({
        chatId: chatId!,
        projectId,
      });
      void trpcUtils.chat.getPaginatedMessages.invalidate({
        chatId: chatId!,
        projectId,
      });
    },
  });

  const { mutate: addMessage } = trpc.chat.addMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void trpcUtils.chat.get.invalidate({
          chatId,
          projectId,
        });
      }
    },
  });

  async function handleCreatePageFromContentSuccess(
    createdFile: FileWithLatestFileVersion,
  ) {
    void trpcUtils.nextJSProject.listPages.invalidate({ projectId });
    await fetchPageFiles([{ id: createdFile.id }]);

    // Automatically switch to the created page in preview panel
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("pageId", createdFile.id);
    setSearchParams(newSearchParams);

    // Update the message to mark as completed with created page ID
    if (projectId && chatId && toolMessageId) {
      updateMessage({
        id: toolMessageId,
        projectId,
        content: [
          {
            type: "tool-result",
            toolCallId: toolResult.toolCallId,
            toolName: toolResult.toolName,
            result: {
              ...toolResult.result,
              createdPageId: createdFile.id,
              isCompleted: true,
            },
          },
        ],
      });

      // Add a user message about the created page
      const generatedPageItem: ChatGeneratedPageItem = {
        type: "generatedPage",
        pageName:
          typeof run?.output?.generatedPage?.pageName === "string"
            ? run.output.generatedPage.pageName
            : run?.output?.generatedPage?.pageName?.pageName ||
              "Generated Page",
        pageId: createdFile.id,
      };

      addMessage({
        projectId,
        chatId,
        content: [
          {
            type: "text",
            contentType: "generatedPage" as const,
            text: JSON.stringify(generatedPageItem),
          },
        ],
        role: "user",
      });
    }
  }

  const { mutate: createPageFromContent } =
    trpc.nextJSProject.createOrUpdatePage.useMutation({
      onSuccess: ({ createdFile }) => {
        void handleCreatePageFromContentSuccess(createdFile);
      },
    });

  useEffect(() => {
    if (
      run?.status === "COMPLETED" &&
      run?.output?.generatedPage &&
      projectId &&
      !isRunCompleted
    ) {
      setIsRunCompleted(true);
      setCompletedRunData(run);

      const pageName =
        typeof run.output.generatedPage.pageName === "string"
          ? run.output.generatedPage.pageName
          : run.output.generatedPage.pageName?.pageName ?? "Generated Page";

      createPageFromContent({
        name: pageName,
        projectId,
        content: run.output.generatedPage.content,
        pageRoute: `/${slugify(pageName)}`,
      });
    }
  }, [run, projectId, createPageFromContent, isRunCompleted]);

  const currentRun = completedRunData ?? run;
  const isRunFailed =
    currentRun?.status === "FAILED" ||
    error ||
    currentRun?.output?.success === false;

  if (isRunFailed) {
    return (
      <div className="text-base px-3 max-w-[350px]">
        <Card className={twMerge("w-full p-0")}>
          <CardContent className="flex flex-col items-start gap-2 p-4">
            <div className="flex flex-row gap-3 items-start">
              <IconButton
                variant="secondary"
                UNSAFE_className="bg-danger-soft border-danger/50"
                icon={<CircleX size={16} className="text-danger" />}
              />
              <div className="flex flex-col gap-1">
                <div className="text-base font-semibold">
                  Page Generation Failed
                </div>
                <div className="text-sm text-muted relative">
                  Something went wrong while generating the page. Please try
                  again.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <PageGenerationCard
      startedAt={currentRun?.startedAt}
      status={currentRun?.status}
    />
  );
}

function CompletedState({ createdPageId }: { createdPageId: string }) {
  const projectId = useCurrentProjectId();
  const isChatLoading = useIsChatLoading();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: page } = trpc.nextJSProject.getPage.useQuery(
    projectId && createdPageId
      ? {
          projectId,
          id: createdPageId,
        }
      : skipToken,
  );

  const handleViewPage = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("pageId", createdPageId);
    setSearchParams(newSearchParams);
  };

  return (
    <PageGenerationCard
      status="COMPLETED"
      pageName={page?.name}
      onViewPage={handleViewPage}
      isChatLoading={isChatLoading}
    />
  );
}

export default function PageGenerationRenderer({
  toolResult,
  toolMessageId,
}: {
  toolResult: Extract<
    ChatMessageContent[number],
    { type: "tool-result" }
  > | null;
  toolMessageId?: string;
}) {
  if (!toolResult) {
    return <PageGenerationCard status="QUEUED" />;
  }

  const createdPageId = toolResult.result.createdPageId;

  if (createdPageId) {
    return <CompletedState createdPageId={createdPageId} />;
  }

  return (
    <GeneratingState toolResult={toolResult} toolMessageId={toolMessageId} />
  );
}
